/**
 * Cross-platform keyboard shortcut utilities
 * Detects the user's platform and provides appropriate modifier keys
 */

const IS_MAC =
  typeof navigator !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0;

/**
 * Get the modifier key symbol for the current platform
 * @returns '⌘' on Mac, 'Ctrl' on Windows/Linux
 */
export const getModifierKey = (): string => (IS_MAC ? "⌘" : "Ctrl");

/**
 * Get the modifier key for display in UI (shorter version)
 * @returns '⌘' on Mac, 'Ctrl' on Windows/Linux
 */
export const getModifierKeyDisplay = (): string => (IS_MAC ? "⌘" : "Ctrl");

/**
 * Get the modifier key for keyboard event checking
 * @returns 'metaKey' on Mac, 'ctrlKey' on Windows/Linux
 */
export const getModifierKeyName = (): "metaKey" | "ctrlKey" => (IS_MAC ? "metaKey" : "ctrlKey");

/**
 * Check if the modifier key (Cmd on Mac, Ctrl on Windows/Linux) is pressed in an event
 * @param event - Keyboard or mouse event
 * @returns true if the platform-specific modifier key is pressed
 */
export const isModifierKeyPressed = (
  event: KeyboardEvent | MouseEvent | React.KeyboardEvent | React.MouseEvent,
): boolean => (IS_MAC ? event.metaKey : event.ctrlKey);

/**
 * Format a keyboard shortcut for display
 * @param keys - Array of keys in the shortcut (e.g., ['mod', 'S'] or ['mod', 'Enter'])
 * @returns Formatted shortcut string (e.g., '⌘ S' on Mac, 'Ctrl S' on Windows)
 */
export const formatShortcut = (keys: string[]): string =>
  keys
    .map(key => {
      if (key.toLowerCase() === "mod" || key.toLowerCase() === "cmd") {
        return getModifierKey();
      }
      if (key.toLowerCase() === "enter" || key === "↩") {
        return "↩";
      }
      if (key.toLowerCase() === "shift") {
        return IS_MAC ? "⇧" : "Shift";
      }
      if (key.toLowerCase() === "alt" || key.toLowerCase() === "option") {
        return IS_MAC ? "⌥" : "Alt";
      }
      return key;
    })
    .join(" ");

/**
 * Format a shortcut with a plus separator (commonly used in tooltips)
 * @param keys - Array of keys in the shortcut
 * @returns Formatted shortcut string with + separator (e.g., '⌘ + S' on Mac, 'Ctrl + S' on Windows)
 */
export const formatShortcutWithPlus = (keys: string[]): string =>
  keys
    .map(key => {
      if (key.toLowerCase() === "mod" || key.toLowerCase() === "cmd") {
        return getModifierKey();
      }
      if (key.toLowerCase() === "enter") {
        return "Enter";
      }
      if (key.toLowerCase() === "shift") {
        return IS_MAC ? "⇧" : "Shift";
      }
      if (key.toLowerCase() === "alt" || key.toLowerCase() === "option") {
        return IS_MAC ? "⌥" : "Alt";
      }
      return key;
    })
    .join(" + ");

/**
 * Check if a keyboard event matches a shortcut
 * @param event - Keyboard event
 * @param keys - Object with modifier and key requirements
 * @returns true if the event matches the shortcut
 */
export const matchesShortcut = (
  event: KeyboardEvent | React.KeyboardEvent,
  keys: {
    mod?: boolean;
    shift?: boolean;
    alt?: boolean;
    key: string;
  },
): boolean => {
  const modPressed = keys.mod ? isModifierKeyPressed(event) : !event.metaKey && !event.ctrlKey;
  const shiftPressed = keys.shift ? event.shiftKey : !event.shiftKey;
  const altPressed = keys.alt ? event.altKey : !event.altKey;
  const keyMatch = event.key.toLowerCase() === keys.key.toLowerCase();

  return modPressed && shiftPressed && altPressed && keyMatch;
};

/**
 * Common keyboard shortcuts
 */
export const SHORTCUTS = {
  SAVE: {
    keys: ["mod", "S"],
    display: formatShortcut(["mod", "S"]),
    displayWithPlus: formatShortcutWithPlus(["mod", "S"]),
  },
  SUBMIT: {
    keys: ["mod", "Enter"],
    display: formatShortcut(["mod", "↩"]),
    displayWithPlus: formatShortcutWithPlus(["mod", "Enter"]),
  },
  SEARCH: {
    keys: ["mod", "K"],
    display: formatShortcut(["mod", "K"]),
    displayWithPlus: formatShortcutWithPlus(["mod", "K"]),
  },
} as const;
